/**
 * LogoUploadStep - Design System v2
 * Matching Login page aesthetic with glassmorphism
 * OPTIONNEL - Upload du logo avec drag & drop effect
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, SkipForward, Loader2, Upload, Image, CheckCircle } from 'lucide-react';
import { companyAPI } from '@/utils/api';
import { logger } from '@/utils/logger';

interface LogoUploadStepProps {
  onComplete: () => void;
  onSkip: () => void;
}

// Glass Button component
const GlassButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
  isLoading?: boolean;
  className?: string;
}> = ({ children, onClick, disabled, variant = 'primary', isLoading, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || isLoading}
    className={`
      flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300
      ${variant === 'primary'
        ? 'bg-gradient-to-r from-lime-400 to-lime-500 text-teal-800 hover:shadow-lg hover:shadow-lime-500/30 hover:-translate-y-0.5 disabled:opacity-50'
        : variant === 'ghost'
        ? 'text-white/60 hover:text-white hover:bg-white/10'
        : 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 hover:border-white/30'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      ${className}
    `}
  >
    {isLoading ? (
      <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
    ) : children}
  </button>
);

export const LogoUploadStep: React.FC<LogoUploadStepProps> = ({ onComplete, onSkip }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = async (file: File) => {
    if (!file) return;

    // Validation
    if (!file.type.startsWith('image/')) {
      setError(t('onboarding.logo.selectImage'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError(t('onboarding.logo.maxSize'));
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    try {
      setIsLoading(true);
      setError(null);

      const companies = await companyAPI.getAll();

      if (!companies || companies.length === 0) {
        throw new Error(t('onboarding.logo.noCompany'));
      }

      const company = companies[0];
      const formData = new FormData();
      formData.append('logo', file);

      await companyAPI.uploadLogo(company.id, formData);

      logger.log('✅ Logo uploadé avec succès');
      await onComplete();
    } catch (err: any) {
      logger.error('❌ Erreur upload logo:', err);
      setError(err.message || t('onboarding.logo.uploadError'));
      setPreviewUrl(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileChange(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          {t('onboarding.logo.title')}
        </h1>
        <p className="text-white/60">
          {t('onboarding.logo.subtitle')}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main card (2/3) */}
        <div className="lg:col-span-2">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
            {error && (
              <div className="mb-6 p-4 bg-red-500/20 backdrop-blur-md border border-red-400/30 rounded-2xl text-white">
                {error}
              </div>
            )}

            <label className="block w-full cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileChange(file);
                }}
                disabled={isLoading}
                className="hidden"
              />
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`relative p-16 border-2 border-dashed rounded-3xl transition-all duration-300 ${
                  isDragging
                    ? 'border-lime-400 bg-lime-500/10'
                    : previewUrl
                    ? 'border-lime-400/50 bg-lime-500/5'
                    : 'border-white/20 hover:border-lime-400/50 hover:bg-white/5'
                }`}
              >
                {previewUrl ? (
                  <div className="flex flex-col items-center">
                    <div className="w-40 h-40 mb-6 rounded-2xl overflow-hidden border-2 border-lime-400/30 bg-white/10 backdrop-blur-md flex items-center justify-center">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="flex items-center gap-2 text-lime-400 mb-2">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">{t('onboarding.logo.uploadSuccess')}</span>
                    </div>
                    <p className="text-sm text-white/50">
                      {t('onboarding.logo.clickToChange')}
                    </p>
                  </div>
                ) : isLoading ? (
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-2xl bg-lime-500/20 flex items-center justify-center mb-4">
                      <Loader2 className="w-8 h-8 text-lime-400 animate-spin" />
                    </div>
                    <p className="text-white font-medium">
                      {t('onboarding.logo.uploading')}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="w-20 h-20 mb-6 flex items-center justify-center bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
                      <Upload className="w-10 h-10 text-lime-400" />
                    </div>
                    <p className="text-white font-semibold mb-2 text-lg">
                      {t('onboarding.logo.dragHere')}
                    </p>
                    <p className="text-white/60 text-sm mb-4">
                      {t('onboarding.logo.orClick')}
                    </p>
                    <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full">
                      <Image className="w-4 h-4 text-white/60" />
                      <span className="text-sm text-white/60">{t('onboarding.logo.formats')}</span>
                    </div>
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>

        {/* Sidebar (1/3) */}
        <div className="space-y-4">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-lime-500/20 flex items-center justify-center">
                <Sparkles size={16} className="text-lime-400" />
              </div>
              <h3 className="text-base font-semibold text-white">
                {t('onboarding.logo.tips')}
              </h3>
            </div>
            <ul className="text-sm text-white/60 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-lime-400">•</span>
                {t('onboarding.logo.tip1')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime-400">•</span>
                {t('onboarding.logo.tip2')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lime-400">•</span>
                {t('onboarding.logo.tip3')}
              </li>
            </ul>
          </div>

          <div className="text-sm text-white/40 space-y-2">
            <p>{t('onboarding.logo.optional')}</p>
          </div>

          <GlassButton
            onClick={onSkip}
            disabled={isLoading}
            variant="ghost"
            className="w-full"
          >
            <SkipForward className="w-5 h-5" />
            {t('onboarding.logo.skip')}
          </GlassButton>
        </div>
      </div>
    </div>
  );
};
